import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Plus, Edit, Trash2, LogIn, Search, Building2, Users, UserCheck, UserX, Calendar, ArrowUpDown, Gift, Globe, Check, ChevronsUpDown, Wallet, ExternalLink, DollarSign, CheckCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import TimezoneSelect from '@/components/TimezoneSelect';
import { useLanguage } from '@/i18n/LanguageContext';

// List of all countries
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria",
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia",
  "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
  "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea North", "Korea South", "Kuwait",
  "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru",
  "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan",
  "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa",
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
  "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
  "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

function CompaniesPage({ currentUser }) {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const memberType = currentUser?.member_type || 'superadmin';
  const isFullSuperadmin = !currentUser?.member_type || currentUser?.member_type === 'superadmin';
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form states
  const [companyName, setCompanyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [companyStatus, setCompanyStatus] = useState('active');
  const [freeStaff, setFreeStaff] = useState(2);
  const [country, setCountry] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [companyTimezone, setCompanyTimezone] = useState('UTC');
  const [emailVerified, setEmailVerified] = useState(true);
  const [formError, setFormError] = useState('');
  
  // Balance modal states
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [balanceCompany, setBalanceCompany] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceDescription, setBalanceDescription] = useState('');
  const [balanceSuccess, setBalanceSuccess] = useState(null);
  
  useEffect(() => {
    fetchCompanies();
  }, []);
  
  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/companies`, {
        withCredentials: true
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setFormError('');
    setActionLoading(true);
    
    try {
      await axios.post(
        `${API}/superadmin/companies`,
        {
          name: companyName,
          admin_name: adminName,
          admin_email: adminEmail,
          admin_password: adminPassword,
          free_staff: freeStaff,
          country: country || 'Unknown',
          timezone: companyTimezone || 'UTC'
        },
        { withCredentials: true }
      );
      
      resetForm();
      setCreateDialogOpen(false);
      fetchCompanies();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleEditCompany = async (e) => {
    e.preventDefault();
    setFormError('');
    setActionLoading(true);
    
    try {
      const updateData = {
        name: companyName,
        admin_name: adminName,
        admin_email: adminEmail,
        status: companyStatus,
        free_staff: freeStaff,
        country: country || 'Unknown',
        timezone: companyTimezone || 'UTC',
        email_verified: emailVerified
      };
      
      if (adminPassword) {
        updateData.admin_password = adminPassword;
      }
      
      await axios.put(
        `${API}/superadmin/companies/${selectedCompany.company_id}`,
        updateData,
        { withCredentials: true }
      );
      
      setEditDialogOpen(false);
      setSelectedCompany(null);
      resetForm();
      fetchCompanies();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDeleteCompany = async () => {
    setActionLoading(true);
    try {
      await axios.delete(
        `${API}/superadmin/companies/${selectedCompany.company_id}`,
        { withCredentials: true }
      );
      
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      console.error('Failed to delete company:', error);
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleBackdoorLogin = async (company) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/superadmin/backdoor/${company.company_id}`,
        {},
        { withCredentials: true }
      );
      
      // Open admin dashboard in new tab with backdoor token in URL
      // Use full URL to ensure query params are preserved
      const backdoorUrl = `${window.location.origin}/admin/dashboard?backdoor_token=${response.data.session_token}`;
      console.log('Opening backdoor URL:', backdoorUrl);
      window.open(backdoorUrl, '_blank');
    } catch (error) {
      console.error('Backdoor login failed:', error);
      alert('Backdoor login failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };
  
  const openBalanceDialog = (company) => {
    setBalanceCompany(company);
    setBalanceAmount('');
    setBalanceDescription('');
    setBalanceSuccess(null);
    setFormError('');
    setBalanceDialogOpen(true);
  };
  
  const handleAddBalance = async (e) => {
    e.preventDefault();
    setFormError('');
    setActionLoading(true);
    
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount <= 0) {
      setFormError('Please enter a valid amount greater than 0');
      setActionLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/superadmin/companies/${balanceCompany.company_id}/add-balance`,
        {
          amount: amount,
          description: balanceDescription || `Manual balance added`
        },
        { withCredentials: true }
      );
      
      setBalanceSuccess({
        amount: response.data.amount,
        newBalance: response.data.new_balance,
        currency: response.data.currency
      });
      
      // Refresh companies list
      fetchCompanies();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to add balance');
    } finally {
      setActionLoading(false);
    }
  };
  
  const resetForm = () => {
    setCompanyName('');
    setAdminName('');
    setAdminEmail('');
    setAdminPassword('');
    setCompanyStatus('active');
    setFreeStaff(2);
    setCountry('');
    setCompanyTimezone('UTC');
    setFormError('');
  };
  
  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setCompanyName(company.name);
    setAdminName(company.admin_name || '');
    setAdminEmail(company.admin_email || '');
    setAdminPassword('');
    setCompanyStatus(company.status || 'active');
    setFreeStaff(company.free_staff || 2);
    setCountry(company.country || '');
    setCompanyTimezone(company.timezone || 'UTC');
    setEmailVerified(company.email_verified !== false);
    setFormError('');
    setEditDialogOpen(true);
  };
  
  const openDeleteDialog = (company) => {
    setSelectedCompany(company);
    setDeleteDialogOpen(true);
  };
  
  // Filter and sort companies
  const filteredAndSortedCompanies = useMemo(() => {
    let result = companies.filter((company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.admin_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.country || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'staff_count':
        result.sort((a, b) => (b.total_staff || 0) - (a.total_staff || 0));
        break;
      case 'attendance':
        result.sort((a, b) => (b.attendance_rate || 0) - (a.attendance_rate || 0));
        break;
      default:
        break;
    }
    
    return result;
  }, [companies, searchTerm, sortBy]);
  
  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700 border-green-200',
      inactive: 'bg-gray-100 text-gray-700 border-gray-200',
      expired: 'bg-red-100 text-red-700 border-red-200'
    };
    return styles[status] || styles.active;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // Calculate summary stats
  const totalStaff = companies.reduce((sum, c) => sum + (c.total_staff || 0), 0);
  const totalPresent = companies.reduce((sum, c) => sum + (c.present_today || 0), 0);
  const totalAbsent = companies.reduce((sum, c) => sum + (c.absent_today || 0), 0);
  
  // Country selector component
  const CountrySelector = ({ value, onChange, id }) => (
    <Popover open={countryOpen} onOpenChange={setCountryOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={countryOpen}
          className="w-full justify-between font-normal"
          data-testid={id}
        >
          {value ? (
            <span className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-500" />
              {value}
            </span>
          ) : (
            <span className="text-gray-500">Select country...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => {
                    onChange(c);
                    setCountryOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === c ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="companies-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t("companies.title")}</h2>
          <p className="text-gray-600 mt-1">Manage all companies in the system</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
          {isFullSuperadmin && (
          <DialogTrigger asChild>
            <Button data-testid="add-company-button" className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Company
            </Button>
          </DialogTrigger>
          )}
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Company</DialogTitle>
              <DialogDescription>
                Add a new company with admin credentials
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCompany} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name</Label>
                <Input
                  id="company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Inc."
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <CountrySelector value={country} onChange={setCountry} id="country-select" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <TimezoneSelect 
                  value={companyTimezone} 
                  onChange={setCompanyTimezone}
                  placeholder="Select timezone..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-name">Admin Name</Label>
                <Input
                  id="admin-name"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  placeholder="John Doe"
                  required
                  data-testid="admin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email">Admin Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@company.com"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Admin Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  minLength={6}
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="free-staff">Free Staff</Label>
                <Input
                  id="free-staff"
                  type="number"
                  min="0"
                  value={freeStaff}
                  onChange={(e) => setFreeStaff(parseInt(e.target.value) || 0)}
                  data-testid="free-staff-input"
                />
                <p className="text-xs text-gray-500">Number of free staff members allowed</p>
              </div>
              {formError && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="form-error">
                  {formError}
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={actionLoading} data-testid="create-company-submit">
                  {actionLoading ? 'Creating...' : 'Create Company'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-2 border-indigo-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-indigo-100">
                <Building2 className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
                <p className="text-xs text-gray-500">Total Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-blue-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalStaff}</p>
                <p className="text-xs text-gray-500">{t("dashboard.total_staff")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-green-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-green-100">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalPresent}</p>
                <p className="text-xs text-gray-500">{t("dashboard.present_today")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-red-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-red-100">
                <UserX className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalAbsent}</p>
                <p className="text-xs text-gray-500">{t("dashboard.absent_today")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Search and Table */}
      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Search className="w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search companies, email, or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
                data-testid="search-companies-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px]" data-testid="sort-select">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Sort by New Signup</SelectItem>
                  <SelectItem value="staff_count">Sort by High Staff Count</SelectItem>
                  <SelectItem value="attendance">Sort by High Attendance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Company Name</TableHead>
                <TableHead className="font-semibold">Country</TableHead>
                <TableHead className="font-semibold">Timezone</TableHead>
                <TableHead className="font-semibold text-center">{t("payments.balance")}</TableHead>
                <TableHead className="font-semibold text-center">Free Staff</TableHead>
                <TableHead className="font-semibold text-center">{t("dashboard.total_staff")}</TableHead>
                <TableHead className="font-semibold text-center">Today Present</TableHead>
                <TableHead className="font-semibold">Signup Date</TableHead>
                <TableHead className="font-semibold">{t("common.status")}</TableHead>
                <TableHead className="font-semibold text-center">Verification</TableHead>
                <TableHead className="text-right font-semibold">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-gray-500 py-8">
                    No companies found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedCompanies.map((company) => (
                  <TableRow key={company.company_id} data-testid={`company-row-${company.company_id}`} className="hover:bg-gray-50">
                    <TableCell>
                      <div>
                        <p className="font-semibold text-gray-900">{company.name}</p>
                        <p className="text-xs text-gray-500">{company.admin_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{company.country || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{company.timezone || 'UTC'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(memberType === 'superadmin' || memberType === 'finance') ? (
                      <button
                        onClick={() => openBalanceDialog(company)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${
                          (company.balance || 0) < 10 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                        data-testid={`balance-${company.company_id}`}
                        title="Click to add balance"
                      >
                        <Wallet className="w-3 h-3" />
                        {company.currency || 'USD'} {(company.balance || 0).toFixed(2)}
                      </button>
                      ) : (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold ${
                          (company.balance || 0) < 10 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        <Wallet className="w-3 h-3" />
                        {company.currency || 'USD'} {(company.balance || 0).toFixed(2)}
                      </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        <Gift className="w-3 h-3" />
                        {company.free_staff || 2}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-blue-600">{company.total_staff || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-green-600">{company.present_today || 0}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{formatDate(company.created_at)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(company.status || 'active')}`}>
                        {(company.status || 'active').charAt(0).toUpperCase() + (company.status || 'active').slice(1)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {company.email_verified === false ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200" data-testid={`verification-${company.company_id}`}>
                          Pending
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`verification-${company.company_id}`}>
                          Verified
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {(memberType === 'superadmin' || memberType === 'operations') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleBackdoorLogin(company)}
                          title="Backdoor Login (opens in new tab)"
                          disabled={actionLoading || company.status !== 'active'}
                          className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          data-testid={`backdoor-${company.company_id}`}
                        >
                          <LogIn className="w-4 h-4 mr-1" />
                          Login
                        </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(company)}
                          className="text-gray-600 hover:text-gray-700"
                          data-testid={`edit-${company.company_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {isFullSuperadmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(company)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-${company.company_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setSelectedCompany(null); resetForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>Update company and admin information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCompany} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Company Name</Label>
              <Input
                id="edit-company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                data-testid="edit-company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-country">Country</Label>
              <CountrySelector value={country} onChange={setCountry} id="edit-country-select" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-timezone">Timezone</Label>
              <TimezoneSelect 
                value={companyTimezone} 
                onChange={setCompanyTimezone}
                placeholder="Select timezone..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-admin-name">Admin Name</Label>
              <Input
                id="edit-admin-name"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                required
                data-testid="edit-admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-admin-email">Admin Email</Label>
              <Input
                id="edit-admin-email"
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                data-testid="edit-admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-admin-password">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-admin-password"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="••••••••"
                minLength={adminPassword ? 6 : 0}
                data-testid="edit-admin-password-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-free-staff">Free Staff</Label>
                <Input
                  id="edit-free-staff"
                  type="number"
                  min="0"
                  value={freeStaff}
                  onChange={(e) => setFreeStaff(parseInt(e.target.value) || 0)}
                  data-testid="edit-free-staff-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">{t("common.status")}</Label>
                <Select value={companyStatus} onValueChange={setCompanyStatus}>
                  <SelectTrigger id="edit-status" data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("common.active")}</SelectItem>
                    <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedCompany?.email_verified === false && (
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="manual-verify-section">
                <div>
                  <p className="text-sm font-medium text-amber-800">Email Not Verified</p>
                  <p className="text-xs text-amber-600">Manually verify if the verification email didn't reach the admin</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={emailVerified ? "default" : "outline"}
                  onClick={() => setEmailVerified(!emailVerified)}
                  className={emailVerified ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  data-testid="manual-verify-toggle"
                >
                  {emailVerified ? 'Verified' : 'Mark Verified'}
                </Button>
              </div>
            )}
            {formError && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="edit-form-error">
                {formError}
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={actionLoading} data-testid="update-company-submit">
                {actionLoading ? 'Updating...' : 'Update Company'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selectedCompany?.name}</strong> and all related data (staff, attendance records). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCompany}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm"
            >
              {actionLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Add Balance Dialog */}
      <Dialog open={balanceDialogOpen} onOpenChange={(open) => { setBalanceDialogOpen(open); if (!open) { setBalanceCompany(null); setBalanceSuccess(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" />
              Add Balance
            </DialogTitle>
            <DialogDescription>
              Add balance to <strong>{balanceCompany?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          {balanceSuccess ? (
            <div className="py-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Balance Added Successfully!</h3>
                <p className="text-gray-600 mb-4">
                  Added <span className="font-semibold text-emerald-600">{balanceSuccess.currency} {balanceSuccess.amount.toFixed(2)}</span>
                </p>
                <div className="bg-gray-50 rounded-lg p-4 w-full">
                  <p className="text-sm text-gray-500">New Balance</p>
                  <p className="text-2xl font-bold text-gray-900">{balanceSuccess.currency} {balanceSuccess.newBalance.toFixed(2)}</p>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button onClick={() => setBalanceDialogOpen(false)} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleAddBalance} className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Current Balance</span>
                  <span className={`text-lg font-bold ${(balanceCompany?.balance || 0) < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {balanceCompany?.currency || 'USD'} {(balanceCompany?.balance || 0).toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="balance-amount">Amount ({balanceCompany?.currency || 'USD'})</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="balance-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={balanceAmount}
                    onChange={(e) => setBalanceAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="pl-9"
                    required
                    data-testid="balance-amount-input"
                  />
                </div>
              </div>
              
              {/* Quick amounts */}
              <div className="space-y-2">
                <Label>Quick Select</Label>
                <div className="flex gap-2">
                  {[10, 25, 50, 100, 200].map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setBalanceAmount(amount.toString())}
                      className="flex-1"
                    >
                      {balanceCompany?.currency || 'USD'} {amount}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="balance-description">Description (optional)</Label>
                <Textarea
                  id="balance-description"
                  value={balanceDescription}
                  onChange={(e) => setBalanceDescription(e.target.value)}
                  placeholder="Reason for adding balance..."
                  rows={2}
                  data-testid="balance-description-input"
                />
              </div>
              
              <div className="bg-blue-50 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-700">
                <Wallet className="w-4 h-4" />
                <span>This will be recorded as a <strong>Manual</strong> payment</span>
              </div>
              
              {formError && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="balance-form-error">
                  {formError}
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setBalanceDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-balance-submit">
                  {actionLoading ? 'Adding...' : 'Add Balance'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CompaniesPage;
